import React, { useEffect, useState } from "react";
import { updateConsent } from "../lib/analytics";

const STORAGE_KEY = "mda_cookie_consent_v1";

/**
 * Cookie banner (GDPR + Garante Italiano compliant).
 * - Denies all non-essential cookies by default (via Consent Mode v2 in index.html).
 * - Shows on first visit until user makes a choice.
 * - Persists choice in localStorage for 6 months (regenerate banner after that).
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
        return;
      }
      const saved = JSON.parse(raw);
      // Expire after 6 months (Garante recommendation)
      const sixMonths = 1000 * 60 * 60 * 24 * 180;
      if (Date.now() - (saved.ts || 0) > sixMonths) {
        setVisible(true);
        return;
      }
      // Re-apply saved consent to gtag on every load
      updateConsent({ analytics: !!saved.analytics, ads: !!saved.marketing });
    } catch {
      setVisible(true);
    }
  }, []);

  const saveAndClose = (choice) => {
    const record = { ...choice, ts: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch { /* localStorage disabled */ }
    updateConsent({ analytics: !!choice.analytics, ads: !!choice.marketing });
    setVisible(false);
  };

  const openPreferences = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDetails(true);
    setVisible(true);
  };
  // Expose global function so the footer link can reopen the banner
  if (typeof window !== "undefined") {
    window.__mdaOpenCookiePreferences = openPreferences;
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[100] bg-[#0a0a0a] border border-white/15 rounded-2xl shadow-2xl p-6"
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Preferenze cookie"
    >
      <div className="font-mono-label text-white/40 text-[10px] tracking-widest mb-3">Cookie & Privacy</div>
      <h3 className="font-display text-white text-lg font-semibold mb-2">Rispettiamo la tua privacy</h3>
      <p className="text-white/60 text-[13px] leading-relaxed mb-4">
        Usiamo cookie tecnici (necessari) e, previo tuo consenso, cookie analitici e di marketing
        per migliorare l&apos;esperienza sul sito. Puoi modificare la scelta in qualsiasi momento.{" "}
        <a href="/cookie-policy" className="underline" style={{ color: "#d4ff3d" }}>Leggi la Cookie Policy</a>.
      </p>

      {showDetails && (
        <div className="space-y-2 mb-4 pb-4 border-b border-white/10">
          <CookieRow
            label="Cookie tecnici"
            desc="Necessari al funzionamento del sito (sessione, sicurezza). Sempre attivi."
            required
            checked
          />
          <CookieRow
            label="Cookie analitici"
            desc="Ci aiutano a capire come usi il sito (Google Analytics 4, in forma anonima)."
            checked={analytics}
            onChange={setAnalytics}
            testId="cookie-toggle-analytics"
          />
          <CookieRow
            label="Cookie di marketing"
            desc="Personalizzazione pubblicitaria (Meta Pixel, Google Ads, LinkedIn)."
            checked={marketing}
            onChange={setMarketing}
            testId="cookie-toggle-marketing"
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <button
          onClick={() => saveAndClose({ analytics: true, marketing: true })}
          className="btn-primary w-full justify-center text-sm"
          data-testid="cookie-accept-all-btn"
        >
          Accetta tutti
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => saveAndClose({ analytics: false, marketing: false })}
            className="text-sm px-4 py-2 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors"
            data-testid="cookie-reject-btn"
          >
            Rifiuta
          </button>
          {showDetails ? (
            <button
              onClick={() => saveAndClose({ analytics, marketing })}
              className="text-sm px-4 py-2 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors"
              data-testid="cookie-save-btn"
            >
              Salva preferenze
            </button>
          ) : (
            <button
              onClick={() => setShowDetails(true)}
              className="text-sm px-4 py-2 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors"
              data-testid="cookie-customize-btn"
            >
              Personalizza
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CookieRow({ label, desc, checked, onChange, required, testId }) {
  return (
    <label className={`flex items-start gap-3 py-1 ${required ? "opacity-70" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={!!required}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 accent-lime-400"
        data-testid={testId}
      />
      <div className="flex-1">
        <div className="text-white text-[13px] font-medium">{label} {required && <span className="text-white/40 text-[10px]">(sempre attivi)</span>}</div>
        <div className="text-white/50 text-[11px] mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
